'use client';

import { useEffect, useMemo, useState } from 'react';
import { useLocale } from 'next-intl';
import {
  convert,
  formatCurrency,
  resolveCurrency,
  FALLBACK_RATES,
  DEFAULT_CURRENCY,
  type Rates,
} from '@/lib/currency';

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : undefined;
}

export interface UseCurrency {
  /** The visitor's resolved display currency (ISO 4217). */
  target: string;
  /** True once live rates have loaded (UI can still render with fallback before). */
  ready: boolean;
  /** Convert + format an amount stored in `from` currency into the visitor's currency. */
  format: (amount: number, from?: string) => string;
  /** Convert (no formatting) an amount from `from` into the visitor's currency. */
  toLocal: (amount: number, from?: string) => number;
  /** Raw rates table (USD-based), or fallback while loading. */
  rates: Rates;
  /** When the provider last updated the rates (ms), once loaded. */
  updated: number | null;
  /** Where the rates came from (e.g. "open.er-api.com", or "fallback"). */
  source: string | null;
}

/**
 * Resolves the visitor's local currency (from the NEXT_CURRENCY cookie set by
 * middleware, falling back to locale) and loads live FX rates from /api/fx.
 *
 * Usage:
 *   const cur = useCurrency();
 *   cur.format(1200, 'USD'); // -> "kr 12 600" for a Swedish visitor, etc.
 */
export function useCurrency(): UseCurrency {
  const locale = useLocale();
  const [rates, setRates] = useState<Rates>(FALLBACK_RATES);
  const [ready, setReady] = useState(false);
  const [updated, setUpdated] = useState<number | null>(null);
  const [source, setSource] = useState<string | null>(null);

  const target = useMemo(() => {
    const cookieCur = readCookie('NEXT_CURRENCY');
    if (cookieCur) return cookieCur.toUpperCase();
    return resolveCurrency(null, locale) || DEFAULT_CURRENCY;
  }, [locale]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/fx')
      .then(r => r.json())
      .then(data => {
        if (!cancelled && data?.rates) {
          setRates(data.rates as Rates);
          setUpdated(typeof data.updated === 'number' ? data.updated : null);
          setSource(typeof data.source === 'string' ? data.source : null);
          setReady(true);
        }
      })
      .catch(() => {
        // keep fallback rates; mark ready so UI doesn't hang
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo<UseCurrency>(
    () => ({
      target,
      ready,
      rates,
      updated,
      source,
      toLocal: (amount: number, from = DEFAULT_CURRENCY) =>
        convert(amount, from, target, rates),
      format: (amount: number, from = DEFAULT_CURRENCY) =>
        formatCurrency(convert(amount, from, target, rates), target, locale),
    }),
    [target, ready, rates, updated, source, locale]
  );
}
